import { useDisclosure } from '@mantine/hooks';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import CloakFetcher from 'cloak-auth/src/Client/CloakFetcher';
import { useAuthStore } from 'cloak-auth/src/Client/stores';
import { InstanceDTO } from 'gen/model';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import AvatarImageWrapper from '@ui/Components/Avatars/AvatarImageWrapper';
import CustomNoRowsOverlay from '@ui/Components/DataGrid/CustomNoRowsOverlay';
import { dayjs } from '@ui/Components/Dayjs/dayjs-utils';
import { UploadType } from '@ui/Components/Image/UploadType';
import NavigationLink from '@ui/Components/Link/NavigationLink';
import DescriptionModal from '@ui/Components/Modals/DescriptionModal';
import { Advertised, InstanceDescription, InstanceInfo, User } from '@ui/Types/AuthServer';
import Capitalize from '@ui/Utils/Capitalize';
import { useRenkiConfig } from '@ui/Utils/device/installationMetadata';

interface InstanceItemsListProps {
    // Map of instance_role (cloak_role) -> latest activity
    activityByRole?: Record<string, { group_id: string; timestamp: string }>;
    descriptions?: Record<number, InstanceDescription>;
    instances: InstanceDTO[];
}

interface InstanceCardProps {
    descriptionOverride?: InstanceDescription;
    instance: InstanceDTO;
    isFavorite: boolean;
    keycloakRoles?: string[];
    onFavorite: (instance: InstanceDTO) => void;
    // Minutes since last activity if within window, otherwise undefined
    recentMinutes?: number;
    to: string;
}

function InstanceCard({
    descriptionOverride,
    instance,
    isFavorite,
    keycloakRoles,
    onFavorite,
    recentMinutes,
    to,
}: Readonly<InstanceCardProps>) {
    const theme = useTheme();

    // Data fetching (skipped if parent provided descriptionOverride)
    const { data: fetchedInstanceDescription } = useSWR<InstanceDescription>(
        !descriptionOverride && instance.domain ? ['tasks/instance_description', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );
    const instanceDescription = descriptionOverride || fetchedInstanceDescription;
    const { data: instanceInfo } = useSWR<InstanceInfo>(
        instance.domain ? ['tasks/instance_info', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );
    const { data: advertised } = useSWR<Advertised>(
        instance.domain ? ['users/advertised', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );
    const { data: user } = useSWR<User>(
        instance.domain ? ['users/timetableprofile/', instance.id] : null,
        (route: string) => CloakFetcher(route, instance.domain),
    );

    // Modal state for DescriptionModal
    const [descriptionModalOpen, { close: handleCloseDescriptionModal, open: handleOpenDescriptionModal }] =
        useDisclosure(false);

    // Settings menu state
    const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
    const settingsMenuOpen = Boolean(settingsAnchorEl);
    const handleSettingsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setSettingsAnchorEl(event.currentTarget);
    };
    const handleSettingsMenuClose = () => {
        setSettingsAnchorEl(null);
    };

    // Role and admin logic
    const instanceSuperuserRole = (instanceInfo?.instance_role ?? '') + '_su';
    const isSuperuser = keycloakRoles?.includes(instanceSuperuserRole);
    const adminCompanies = user?.user_companies.filter((item) => user.admin.company.includes(item.id));

    const name = instanceDescription?.name ?? instance.name;
    const type = instanceDescription?.instance_type ? Capitalize(instanceDescription.instance_type) : 'Location';
    const address = instanceDescription?.address ? Capitalize(instanceDescription.address) : '';
    const description = instanceDescription?.description ?? 'No description available';
    const isOnline = !!instanceInfo;
    const isPublic = instanceDescription?.public_operator ?? false;
    const imagePath = instanceDescription?.image;

    const showRecent = typeof recentMinutes === 'number' && recentMinutes <= 10;
    const recentLabel =
        typeof recentMinutes === 'number'
            ? recentMinutes <= 5
                ? 'Seen now'
                : `Seen ${recentMinutes.toString()} min ago`
            : undefined;

    // Compact chip styling
    const compactChipSx = {
        '& .MuiChip-icon': { color: 'inherit', fontSize: 14 },
        '& .MuiChip-label': { fontSize: 11, fontWeight: 600, lineHeight: 1.2, px: 0.75 },
        borderRadius: 1.5,
        height: 22,
    } as const;

    return (
        <Card
            sx={{
                '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-1px)',
                },
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.3s ease-in-out',
            }}
        >
            {/* Activity indicator - top-left */}
            {showRecent ? (
                <Box
                    sx={{
                        display: 'flex',
                        left: 12,
                        position: 'absolute',
                        top: 12,
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            backdropFilter: 'blur(8px)',
                            backgroundColor: alpha(theme.palette.common.black, 0.6),
                            borderRadius: 2,
                            display: 'flex',
                            p: 0.5,
                        }}
                    >
                        <Chip
                            icon={<PersonIcon fontSize="small" />}
                            label={recentLabel}
                            size="small"
                            sx={{
                                ...compactChipSx,
                                backgroundColor:
                                    recentMinutes <= 5
                                        ? alpha(theme.palette.mlGreen.main, 0.9)
                                        : alpha(theme.palette.mlYellow.main, 0.9),
                                color: theme.palette.common.white,
                            }}
                        />
                    </Box>
                </Box>
            ) : null}

            {/* Status + privacy - top-right */}
            <Box
                sx={{
                    display: 'flex',
                    position: 'absolute',
                    right: 12,
                    top: 12,
                    zIndex: 2,
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        backdropFilter: 'blur(8px)',
                        backgroundColor: alpha(theme.palette.common.black, 0.6),
                        borderRadius: 2,
                        display: 'flex',
                        gap: 0.75,
                        p: 0.5,
                    }}
                >
                    <Chip
                        icon={isPublic ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                        label={isPublic ? 'Public' : 'Private'}
                        size="small"
                        sx={{
                            ...compactChipSx,
                            backgroundColor: isPublic
                                ? alpha(theme.palette.success.main, 0.9)
                                : alpha(theme.palette.warning.main, 0.9),
                            color: theme.palette.common.white,
                        }}
                        variant="filled"
                    />
                    <Tooltip title={isOnline ? 'Instance is online' : 'Instance is offline'}>
                        <Chip
                            icon={
                                isOnline ? (
                                    <CheckCircleOutlinedIcon fontSize="small" />
                                ) : (
                                    <HighlightOffIcon fontSize="small" />
                                )
                            }
                            label={isOnline ? 'Online' : 'Offline'}
                            size="small"
                            sx={{
                                ...compactChipSx,
                                backgroundColor: isOnline
                                    ? alpha(theme.palette.success.main, 0.9)
                                    : alpha(theme.palette.error.main, 0.9),
                                color: theme.palette.common.white,
                            }}
                        />
                    </Tooltip>
                </Box>
            </Box>

            {/* Clickable card content */}
            <NavigationLink to={to}>
                <Box
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        transition: 'all 0.3s ease-in-out',
                    }}
                >
                    {/* Image section */}
                    <Box
                        sx={{
                            aspectRatio: '16/9',
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                        }}
                    >
                        <AvatarImageWrapper
                            imagePath={imagePath}
                            imageType={UploadType.Instance}
                            isLoading={false}
                            sx={{
                                '& img': {
                                    objectFit: 'cover',
                                },
                                height: '100%',
                                width: '100%',
                            }}
                            variant="square"
                        />
                        <Box
                            sx={{
                                alignItems: 'flex-end',
                                background: `linear-gradient(transparent, ${alpha(theme.palette.common.black, 0.7)})`,
                                bottom: 0,
                                display: 'flex',
                                height: 60,
                                left: 0,
                                p: 2,
                                position: 'absolute',
                                right: 0,
                            }}
                        >
                            <Typography
                                sx={{
                                    color: theme.palette.common.white,
                                    fontWeight: 600,
                                    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                }}
                                variant="h6"
                            >
                                {name || instance.name}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Content section (clickable for navigation) */}
                    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', p: 2, pb: 0 }}>
                        {/* Type and Address */}
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
                            <LocationOnIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                            <Typography sx={{ color: theme.palette.text.secondary }} variant="body2">
                                {type} {address ? ' • ' + address : ''}
                            </Typography>
                        </Stack>

                        {/* Description */}
                        <Typography
                            sx={{
                                color: theme.palette.text.secondary,
                                display: '-webkit-box',
                                flex: 1,
                                lineHeight: 1.4,
                                mb: 2,
                                minHeight: '2.8em',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                            }}
                            variant="body2"
                        >
                            {description || 'No description available.'}
                        </Typography>

                        <Divider sx={{ mb: 2 }} />
                    </Box>
                </Box>
            </NavigationLink>

            {/* Action buttons (non-clickable for navigation) */}
            <CardContent sx={{ p: 2, pt: 0 }}>
                <Stack
                    direction="row"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                    spacing={1}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onFavorite(instance);
                                }}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    },
                                    color: isFavorite ? theme.palette.error.main : theme.palette.text.secondary,
                                }}
                            >
                                {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="View information">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDescriptionModal();
                                }}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                                    },
                                    color: theme.palette.info.main,
                                }}
                            >
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Tooltip title="Settings">
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSettingsMenu(e);
                            }}
                            sx={{
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                                color: theme.palette.text.secondary,
                            }}
                        >
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </CardContent>

            {/* Settings Menu */}
            <Menu
                anchorEl={settingsAnchorEl}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                onClick={handleSettingsMenuClose}
                onClose={handleSettingsMenuClose}
                open={settingsMenuOpen}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                {/* Instance Settings Section */}
                <ListSubheader sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 2.5 }}>Instance</ListSubheader>
                <NavigationLink to={`/settings?tab=instances&id=${instance.id}`}>
                    <MenuItem>
                        <ListItemIcon>
                            <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText>Settings</ListItemText>
                    </MenuItem>
                </NavigationLink>

                {/* Operator Settings Section */}
                {isSuperuser ? (
                    <Box>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 2.5 }}>
                            Operator Settings
                        </ListSubheader>
                        <NavigationLink to={`${instance.domain}/admin/instance`}>
                            <MenuItem disabled={!instanceInfo}>
                                <ListItemIcon>
                                    <AdminPanelSettingsIcon />
                                </ListItemIcon>
                                <ListItemText>Instance Settings</ListItemText>
                            </MenuItem>
                        </NavigationLink>
                    </Box>
                ) : null}

                {/* Company Settings Section */}
                {adminCompanies && adminCompanies.length > 0 ? (
                    <Box>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 2.5 }}>
                            Company Settings
                        </ListSubheader>
                        {adminCompanies.map((item) => (
                            <NavigationLink key={item.id} to={`${instance.domain}/admin/managecompanies/${item.id}`}>
                                <MenuItem disabled={!instanceInfo}>
                                    <ListItemIcon>
                                        <ApartmentIcon />
                                    </ListItemIcon>
                                    <ListItemText>{item.name}</ListItemText>
                                </MenuItem>
                            </NavigationLink>
                        ))}
                    </Box>
                ) : null}
            </Menu>

            {descriptionModalOpen ? (
                <DescriptionModal
                    advertised={advertised}
                    domain={instance.domain}
                    excludedKeys={['id', 'public_operator']}
                    instanceDescription={instanceDescription}
                    instanceId={instance.id}
                    instanceInfo={instanceInfo}
                    onClose={handleCloseDescriptionModal}
                    open={descriptionModalOpen}
                    showUserAmount={false}
                    tabs
                />
            ) : null}
        </Card>
    );
}

export default function InstanceList({ activityByRole, descriptions, instances }: Readonly<InstanceItemsListProps>) {
    const theme = useTheme();
    const { config, updateConfig } = useRenkiConfig();

    // Get Keycloak roles from authStore
    const keycloakToken = useAuthStore.getState().decodedToken;
    const keycloakRoles = keycloakToken?.realm_access.roles;

    // Compute minutes since last activity per role (hook before any early returns)
    const getRecentMinutes = useCallback(
        (role?: string): number | undefined => {
            if (!role || !activityByRole) {
                return undefined;
            }
            const meta = activityByRole[role];
            if (!meta?.timestamp) {
                return undefined;
            }
            const diff = dayjs().diff(dayjs(meta.timestamp), 'minute');
            return diff >= 0 ? diff : undefined;
        },
        [activityByRole],
    );

    // Build non-favorite and sorted list (hooks before early returns)
    const nonFavorite = useMemo(
        () => instances.filter((instance) => instance.id !== config?.favoriteInstance),
        [instances, config?.favoriteInstance],
    );

    const sortedNonFavorite = useMemo(() => {
        return [...nonFavorite]
            .map((inst, index) => {
                const minutes = getRecentMinutes(inst.cloak_role);
                const isActive = typeof minutes === 'number' && minutes <= 10;
                const rank = isActive ? minutes : 100_000 + index;
                return { inst, rank };
            })
            .sort((a, b) => a.rank - b.rank)
            .map((x) => x.inst);
    }, [nonFavorite, getRecentMinutes]);

    const handleFavorite = useCallback(
        (instance: InstanceDTO) => {
            if (instance.id !== config?.favoriteInstance) {
                void updateConfig({ defaultLander: instance.domain, favoriteInstance: instance.id });
            } else {
                void updateConfig({ defaultLander: undefined, favoriteInstance: undefined });
            }
        },
        [updateConfig, config?.favoriteInstance],
    );

    if (!config) {
        return <CustomNoRowsOverlay text="Loading..." />;
    }

    if (instances.length === 0) {
        return <CustomNoRowsOverlay text="No places found." />;
    }

    const favoriteInstance = instances.find((instance) => instance.id === config.favoriteInstance);
    const getDescription = (id: InstanceDTO['id']): InstanceDescription | undefined => {
        const numericId = typeof id === 'number' ? id : Number(id);
        return descriptions ? (descriptions as unknown as Record<number, InstanceDescription>)[numericId] : undefined;
    };

    return (
        <Box sx={{ px: 1, py: 2 }}>
            {/* Favorite Section */}
            {favoriteInstance ? (
                <Box sx={{ mb: 4 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 3 }}>
                        <FavoriteIcon sx={{ color: theme.palette.error.main }} />
                        <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600 }} variant="h6">
                            Favorite Place
                        </Typography>
                    </Stack>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 3,
                            gridTemplateColumns: {
                                lg: 'repeat(3, 1fr)',
                                md: 'repeat(3, 1fr)',
                                sm: 'repeat(2, 1fr)',
                                xs: '1fr',
                            },
                        }}
                    >
                        <InstanceCard
                            descriptionOverride={getDescription(favoriteInstance.id)}
                            instance={favoriteInstance}
                            isFavorite
                            keycloakRoles={keycloakRoles}
                            onFavorite={handleFavorite}
                            recentMinutes={getRecentMinutes(favoriteInstance.cloak_role)}
                            to={favoriteInstance.domain || '#'}
                        />
                    </Box>
                </Box>
            ) : null}

            {/* All Places Section */}
            <Box>
                <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 3 }} variant="h6">
                    All Places
                </Typography>
                {nonFavorite.length === 0 ? (
                    <CustomNoRowsOverlay text="No other places available." />
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 3,
                            gridTemplateColumns: {
                                lg: 'repeat(3, 1fr)',
                                md: 'repeat(3, 1fr)',
                                sm: 'repeat(2, 1fr)',
                                xs: '1fr',
                            },
                        }}
                    >
                        {sortedNonFavorite.map((instance) => (
                            <InstanceCard
                                descriptionOverride={getDescription(instance.id)}
                                instance={instance}
                                isFavorite={instance.id === config.favoriteInstance}
                                key={instance.id}
                                keycloakRoles={keycloakRoles}
                                onFavorite={handleFavorite}
                                recentMinutes={getRecentMinutes(instance.cloak_role)}
                                to={instance.domain || '#'}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
